import React, { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import {
  useGetPaymentProfilesQuery,
  useAddPaymentProfileMutation,
  useSetDefaultCardMutation,
  useRemoveCardMutation,
} from "../../../Api/platformApi";
import {
  AppPage,
  PageHeader,
  CardSurface,
  ActionButton,
  StatusBadge,
} from "../../../Components/ui";
import {
  NAVY,
  NAVY_DARK,
  GOLD,
  GOLD_SOFT,
  TEXT_SECONDARY,
  ERROR,
} from "../../../theme/tokens";

const PaymentMethods: React.FC = () => {
  const { data } = useGetPaymentProfilesQuery();
  const [addCard, { isLoading }] = useAddPaymentProfileMutation();
  const [setDefault] = useSetDefaultCardMutation();
  const [removeCard] = useRemoveCardMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({
    number: "",
    cvd: "",
    expiry_month: "",
    expiry_year: "",
    name: "",
  });
  const [error, setError] = useState<string | null>(null);

  const cards = data?.data ?? [];

  const save = async () => {
    setError(null);
    try {
      await addCard(form).unwrap();
      setOpen(false);
      setForm({ number: "", cvd: "", expiry_month: "", expiry_year: "", name: "" });
    } catch (e: any) {
      setError(e?.data?.message || "Could not save card");
    }
  };

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<CreditCardRoundedIcon />}
          title="Payment Methods"
          subtitle="Manage your saved payment cards"
          actions={
            <ActionButton
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setError(null);
                setOpen(true);
              }}
            >
              Add Card
            </ActionButton>
          }
        />
      }
    >
      <Alert severity="info" sx={{ mb: 3, borderRadius: "10px" }}>
        Cards are tokenized by the payment provider — INTALK stores only the token and the last 4 digits.
      </Alert>

      {cards.length === 0 ? (
        <CardSurface
          noHover
          sx={{
            p: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            textAlign: "center",
          }}
        >
          <CreditCardRoundedIcon sx={{ fontSize: 52, color: alpha(NAVY, 0.18) }} />
          <Typography fontSize={16} fontWeight={700} color={NAVY}>
            No cards on file
          </Typography>
          <Typography fontSize={13} color={TEXT_SECONDARY}>
            Add a card to enable automatic billing and easy payment.
          </Typography>
          <ActionButton
            startIcon={<AddRoundedIcon />}
            onClick={() => {
              setError(null);
              setOpen(true);
            }}
          >
            Add Card
          </ActionButton>
        </CardSurface>
      ) : (
        <Grid container spacing={3}>
          {cards.map((c: any) => (
            <Grid item xs={12} sm={6} md={4} key={c.id}>
              <CardSurface
                sx={{
                  p: 0,
                  overflow: "hidden",
                  ...(c.is_default
                    ? {
                        border: `2px solid ${GOLD}`,
                        boxShadow: `0 0 0 4px ${alpha(GOLD, 0.14)}, 0 8px 24px rgba(0,0,0,0.08)`,
                      }
                    : {}),
                }}
              >
                {/* Card art header */}
                <Stack
                  sx={{
                    p: 2.5,
                    background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
                    borderRadius: "14px 14px 0 0",
                    gap: 2,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      fontSize={11}
                      fontWeight={700}
                      letterSpacing="0.12em"
                      textTransform="uppercase"
                      color={alpha(GOLD_SOFT, 0.85)}
                    >
                      {c.card_brand || "Card"}
                    </Typography>
                    {c.is_default && (
                      <StatusBadge status="active" label="Default" tone="success" size="small" />
                    )}
                  </Stack>

                  <Typography
                    fontSize={18}
                    fontWeight={700}
                    letterSpacing="0.18em"
                    color="#fff"
                    sx={{ fontFamily: "monospace" }}
                  >
                    •••• •••• •••• {c.last_four}
                  </Typography>

                  <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                    {c.name ? (
                      <Typography fontSize={12} color={alpha("#fff", 0.7)} textTransform="uppercase" letterSpacing="0.08em">
                        {c.name}
                      </Typography>
                    ) : (
                      <span />
                    )}
                    <Stack>
                      <Typography fontSize={9} color={alpha("#fff", 0.5)} letterSpacing="0.08em" textTransform="uppercase">
                        Expires
                      </Typography>
                      <Typography fontSize={13} fontWeight={700} color={alpha("#fff", 0.9)}>
                        {c.expiry_month}/{c.expiry_year}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

                {/* Action footer */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  spacing={0.5}
                  sx={{ px: 2, py: 1.5 }}
                >
                  {!c.is_default && (
                    <Tooltip title="Set as default">
                      <IconButton
                        size="small"
                        onClick={() => setDefault(c.id)}
                        sx={{ color: GOLD }}
                      >
                        <StarOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {c.is_default && (
                    <StarRoundedIcon sx={{ fontSize: 18, color: GOLD, mx: 0.5 }} />
                  )}
                  <Tooltip title="Remove card">
                    <IconButton
                      size="small"
                      onClick={() => removeCard(c.id)}
                      sx={{ color: ERROR }}
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardSurface>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Card Dialog — keep existing tokenized submission flow */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add Card</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Cardholder Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Card Number"
              value={form.number}
              onChange={(e) => setForm({ ...form, number: e.target.value })}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="MM"
                value={form.expiry_month}
                onChange={(e) => setForm({ ...form, expiry_month: e.target.value })}
                sx={{ width: 80 }}
              />
              <TextField
                label="YYYY"
                value={form.expiry_year}
                onChange={(e) => setForm({ ...form, expiry_year: e.target.value })}
                sx={{ width: 100 }}
              />
              <TextField
                label="CVV"
                value={form.cvd}
                onChange={(e) => setForm({ ...form, cvd: e.target.value })}
                sx={{ width: 80 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={isLoading || !form.number || !form.cvd}
          >
            Save Card
          </Button>
        </DialogActions>
      </Dialog>
    </AppPage>
  );
};

export default PaymentMethods;
